import React, { useState, useEffect, useRef } from 'react';
import { X, User, Film, Tv, Lock, Globe, Eye, EyeOff, Save, Search, Mail, Bell, BellOff, Upload, Camera, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import axios from 'axios';
import { API_URL } from '../config/api';

const GENRES = [
  { id: 28, name: 'Action' }, { id: 12, name: 'Adventure' }, { id: 16, name: 'Animation' },
  { id: 35, name: 'Comedy' }, { id: 80, name: 'Crime' }, { id: 99, name: 'Documentary' },
  { id: 18, name: 'Drama' }, { id: 10751, name: 'Family' }, { id: 14, name: 'Fantasy' },
  { id: 36, name: 'History' }, { id: 27, name: 'Horror' }, { id: 10402, name: 'Music' },
  { id: 9648, name: 'Mystery' }, { id: 10749, name: 'Romance' }, { id: 878, name: 'Science Fiction' },
  { id: 10770, name: 'TV Movie' }, { id: 53, name: 'Thriller' }, { id: 10752, name: 'War' },
  { id: 37, name: 'Western' }
];

const TMDB_IMAGE_BASE = 'https://image.tmdb.org/t/p/w200';
const TMDB_BEARER_TOKEN = process.env.REACT_APP_TMDB_BEARER_TOKEN;

const ProfileSettings = ({ isOpen, onClose, user, onUpdateUser }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [bio, setBio] = useState('');
  const [location, setLocation] = useState('');
  const [website, setWebsite] = useState('');
  const fileInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const [avatarType, setAvatarType] = useState('initials');
  const [avatarSearch, setAvatarSearch] = useState('');
  const [avatarResults, setAvatarResults] = useState([]);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [searchingAvatars, setSearchingAvatars] = useState(false);

  const [selectedGenres, setSelectedGenres] = useState([]);

  const [privacySettings, setPrivacySettings] = useState({
    profile_public: true, hide_activity: false, posts_public: true, comments_public: true
  });

  const [emailNotifications, setEmailNotifications] = useState({
    club_invites: true, club_posts: true
  });

  useEffect(() => {
    if (user && isOpen) {
      setDisplayName(user.display_name || `${user.first_name} ${user.last_name}`);
      setUsername(user.username || '');
      setBio(user.bio || '');
      setLocation(user.location || '');
      setWebsite(user.website || '');
      setAvatarType(user.avatar_type || 'initials');
      setSelectedAvatar(user.profile_picture ? { image: user.profile_picture, id: user.avatar_tmdb_id } : null);
      setSelectedGenres(user.favorite_genres || []);
      setPrivacySettings(user.privacy_settings || { profile_public: true, hide_activity: false, posts_public: true, comments_public: true });
      setEmailNotifications(user.email_notifications || { club_invites: true, club_posts: true });
      setError('');
      setSuccess('');
    }
  }, [user, isOpen]);

  const searchAvatars = async () => {
    if (!avatarSearch.trim()) return;
    setSearchingAvatars(true);
    try {
      const mediaType = avatarType === 'movie' ? 'movie' : 'tv';
      const res = await axios.get(`https://api.themoviedb.org/3/search/${mediaType}`, {
        params: { query: avatarSearch },
        headers: { Authorization: `Bearer ${TMDB_BEARER_TOKEN}` }
      });
      setAvatarResults(res.data.results.filter(i => i.poster_path).slice(0, 12).map(i => ({
        id: i.id, title: i.title || i.name, image: `${TMDB_IMAGE_BASE}${i.poster_path}`, media_type: mediaType
      })));
    } catch (err) { console.error(err); }
    finally { setSearchingAvatars(false); }
  };

  const handleGenreToggle = (genreId) => {
    setSelectedGenres(prev => prev.includes(genreId) ? prev.filter(id => id !== genreId) : [...prev, genreId]);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('Image must be less than 5MB'); return; }
    setUploadingPhoto(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/api/upload/profile-photo?user_id=${user.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.url) {
        setAvatarType('upload');
        setSelectedAvatar({ image: res.data.url, id: null });
        setSuccess('Photo uploaded!');
      }
    } catch (err) { setError('Failed to upload photo'); }
    finally { setUploadingPhoto(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const updateData = {
        display_name: displayName, username, bio, location, website,
        avatar_type: avatarType, profile_picture: selectedAvatar?.image || user.profile_picture || '',
        avatar_tmdb_id: selectedAvatar?.id || null, favorite_genres: selectedGenres,
        privacy_settings: privacySettings, email_notifications: emailNotifications
      };
      const res = await axios.put(`${API_URL}/api/users/${user.id}`, updateData);
      if (res.data) {
        setSuccess('Profile updated!');
        onUpdateUser({ ...user, ...updateData });
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) { setError(err.response?.data?.detail || 'Failed to update profile'); }
    finally { setSaving(false); }
  };

  const getInitials = () => `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`.toUpperCase();

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'avatar', label: 'Avatar', icon: Film },
    { id: 'genres', label: 'Genres', icon: Tv },
    { id: 'privacy', label: 'Privacy', icon: Lock },
    { id: 'notifications', label: 'Emails', icon: Mail }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50" data-testid="profile-settings-modal">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal — full height on mobile, centered card on desktop */}
      <div className="fixed inset-0 sm:inset-auto sm:top-[50%] sm:left-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:w-[90vw] sm:max-w-2xl sm:max-h-[85dvh] sm:rounded-xl bg-zinc-900 border-0 sm:border sm:border-zinc-700 text-white flex flex-col overflow-hidden z-50">

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700 flex-shrink-0">
          <h2 className="text-base sm:text-lg font-bold">Profile Settings</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-zinc-700 transition-colors" data-testid="settings-close-btn">
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        {/* Tabs — horizontal scroll on mobile */}
        <div className="flex border-b border-zinc-700 flex-shrink-0 overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center justify-center gap-1.5 py-2.5 px-3 sm:px-4 transition-colors whitespace-nowrap flex-shrink-0 ${
                activeTab === tab.id
                  ? 'border-b-2 border-red-600 text-white bg-zinc-800'
                  : 'text-gray-400 hover:text-white'
              }`}
              data-testid={`settings-tab-${tab.id}`}
            >
              <tab.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              <span className="text-xs sm:text-sm">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-4 py-4 sm:py-5">

          {/* ========= PROFILE TAB ========= */}
          {activeTab === 'profile' && (
            <div className="space-y-4">
              <div>
                <Label className="text-gray-300 text-xs sm:text-sm">Display Name</Label>
                <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 sm:h-10 text-sm" placeholder="How you want to be known" />
                <p className="text-gray-500 text-[10px] sm:text-xs mt-1">This is shown instead of your full name</p>
              </div>

              <div>
                <Label className="text-gray-300 text-xs sm:text-sm">Username</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">@</span>
                  <Input value={username} onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                    className="bg-zinc-800 border-zinc-700 text-white pl-8 h-9 sm:h-10 text-sm" placeholder="username" />
                </div>
                <p className="text-gray-500 text-[10px] sm:text-xs mt-1">Letters, numbers, underscores only</p>
              </div>

              <div>
                <Label className="text-gray-300 text-xs sm:text-sm">Email</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input value={user.email || ''} disabled className="bg-zinc-800/50 border-zinc-700 text-gray-500 cursor-not-allowed flex-1 min-w-0 h-9 sm:h-10 text-xs sm:text-sm" data-testid="profile-email" />
                  {user.email_verified ? (
                    <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-green-900/30 border border-green-700/40 text-green-400 text-[10px] sm:text-xs font-medium whitespace-nowrap flex-shrink-0" data-testid="email-verified-badge">
                      <CheckCircle2 className="h-3 w-3" />Verified
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-red-900/30 border border-red-700/40 text-red-400 text-[10px] sm:text-xs font-medium whitespace-nowrap flex-shrink-0" data-testid="email-unverified-badge">
                      <AlertCircle className="h-3 w-3" />Unverified
                    </span>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-gray-300 text-xs sm:text-sm">Bio</Label>
                <Textarea value={bio} onChange={(e) => setBio(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1 min-h-[80px] sm:min-h-[100px] text-sm"
                  placeholder="Tell us about yourself..." maxLength={500} />
                <p className="text-gray-500 text-[10px] sm:text-xs mt-1">{bio.length}/500</p>
              </div>

              <div>
                <Label className="text-gray-300 text-xs sm:text-sm">Location</Label>
                <Input value={location} onChange={(e) => setLocation(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 sm:h-10 text-sm" placeholder="City, Country" />
              </div>

              <div>
                <Label className="text-gray-300 text-xs sm:text-sm">Website</Label>
                <Input value={website} onChange={(e) => setWebsite(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 sm:h-10 text-sm" placeholder="https://yourwebsite.com" />
              </div>
            </div>
          )}

          {/* ========= AVATAR TAB ========= */}
          {activeTab === 'avatar' && (
            <div className="space-y-4">
              <Label className="text-gray-300 text-xs sm:text-sm block">Avatar Type</Label>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { key: 'initials', icon: User, label: 'Initials', action: () => { setAvatarType('initials'); setSelectedAvatar(null); } },
                  { key: 'upload', icon: Upload, label: 'Upload', action: () => fileInputRef.current?.click() },
                  { key: 'movie', icon: Film, label: 'Movie', action: () => setAvatarType('movie') },
                  { key: 'tv', icon: Tv, label: 'TV Show', action: () => setAvatarType('tv') },
                ].map(opt => (
                  <button key={opt.key} onClick={opt.action}
                    className={`p-2 sm:p-3 rounded-lg border text-center transition-colors ${avatarType === opt.key ? 'border-red-600 bg-red-600/20' : 'border-zinc-700 hover:border-zinc-500'}`}>
                    <opt.icon className="h-5 w-5 mx-auto mb-0.5" />
                    <span className="text-[10px] sm:text-xs">{opt.label}</span>
                  </button>
                ))}
              </div>

              <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept="image/*" className="hidden" />

              {uploadingPhoto && (
                <div className="text-center py-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600 mx-auto" />
                  <p className="text-gray-400 text-xs mt-1">Uploading...</p>
                </div>
              )}

              <div className="flex justify-center py-2">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden bg-red-600 flex items-center justify-center text-2xl sm:text-3xl font-bold">
                  {avatarType === 'initials' || !selectedAvatar
                    ? getInitials()
                    : <img src={selectedAvatar.image} alt="Avatar" className="w-full h-full object-cover" />}
                </div>
              </div>

              {(avatarType === 'movie' || avatarType === 'tv') && (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Input value={avatarSearch} onChange={(e) => setAvatarSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && searchAvatars()}
                      className="bg-zinc-800 border-zinc-700 text-white h-9 text-sm"
                      placeholder={`Search ${avatarType === 'movie' ? 'movies' : 'TV shows'}...`} />
                    <Button onClick={searchAvatars} disabled={searchingAvatars} className="bg-red-600 hover:bg-red-700 h-9 px-3">
                      <Search className="h-4 w-4" />
                    </Button>
                  </div>
                  {avatarResults.length > 0 && (
                    <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto">
                      {avatarResults.map(r => (
                        <button key={r.id} onClick={() => setSelectedAvatar(r)}
                          className={`rounded overflow-hidden border-2 transition-all ${selectedAvatar?.id === r.id ? 'border-red-600 scale-105' : 'border-transparent hover:border-zinc-500'}`}>
                          <img src={r.image} alt={r.title} className="w-full aspect-[2/3] object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ========= GENRES TAB ========= */}
          {activeTab === 'genres' && (
            <div className="space-y-3">
              <p className="text-gray-400 text-xs sm:text-sm">Select your favorite genres</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 sm:gap-2">
                {GENRES.map(g => (
                  <button key={g.id} onClick={() => handleGenreToggle(g.id)}
                    className={`p-2 sm:p-3 rounded-lg border text-[11px] sm:text-sm transition-all ${
                      selectedGenres.includes(g.id) ? 'border-red-600 bg-red-600/20 text-white' : 'border-zinc-700 hover:border-zinc-500 text-gray-400'
                    }`}>
                    {g.name}
                  </button>
                ))}
              </div>
              <p className="text-gray-500 text-[10px] sm:text-xs">{selectedGenres.length} selected</p>
            </div>
          )}

          {/* ========= PRIVACY TAB ========= */}
          {activeTab === 'privacy' && (
            <div className="space-y-5">
              {[
                { key: 'profile_public', icon: Globe, title: 'Public Profile', desc: 'Others can see your profile' },
                { key: 'hide_activity', icon: privacySettings.hide_activity ? EyeOff : Eye, title: 'Hide Activity', desc: 'Hide what you are watching' },
                { key: 'posts_public', icon: Globe, title: 'Public Posts', desc: 'Anyone can see your posts' },
                { key: 'comments_public', icon: Globe, title: 'Public Comments', desc: 'Anyone can see your comments' },
              ].map(opt => (
                <div key={opt.key} className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <opt.icon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-white font-medium text-xs sm:text-sm">{opt.title}</p>
                      <p className="text-gray-500 text-[10px] sm:text-xs">{opt.desc}</p>
                    </div>
                  </div>
                  <Switch checked={privacySettings[opt.key]}
                    onCheckedChange={(checked) => setPrivacySettings(prev => ({ ...prev, [opt.key]: checked }))} />
                </div>
              ))}
            </div>
          )}

          {/* ========= NOTIFICATIONS TAB ========= */}
          {activeTab === 'notifications' && (
            <div className="space-y-5">
              <div className="bg-zinc-800 rounded-lg p-3">
                <div className="flex items-center gap-2 text-gray-300">
                  <Mail className="h-4 w-4 text-red-500 flex-shrink-0" />
                  <p className="text-xs sm:text-sm">Manage email notifications</p>
                </div>
              </div>

              {[
                { key: 'club_invites', icon: Bell, color: 'text-purple-500', title: 'Club Invitations', desc: 'Notified when invited to a club' },
                { key: 'club_posts', icon: Bell, color: 'text-blue-500', title: 'Club Posts', desc: 'Notified when members post' },
              ].map(opt => (
                <div key={opt.key} className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <opt.icon className={`h-4 w-4 flex-shrink-0 ${opt.color}`} />
                    <div className="min-w-0">
                      <p className="text-white font-medium text-xs sm:text-sm">{opt.title}</p>
                      <p className="text-gray-500 text-[10px] sm:text-xs">{opt.desc}</p>
                    </div>
                  </div>
                  <Switch checked={emailNotifications[opt.key]}
                    onCheckedChange={(checked) => setEmailNotifications(prev => ({ ...prev, [opt.key]: checked }))} />
                </div>
              ))}

              <div className="border-t border-zinc-700 pt-4">
                <button onClick={() => setEmailNotifications({ club_invites: false, club_posts: false })}
                  className="flex items-center gap-2 text-gray-400 hover:text-red-500 text-xs transition-colors">
                  <BellOff className="h-3.5 w-3.5" />Turn off all notifications
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer — sticky at bottom */}
        <div className="px-4 py-3 border-t border-zinc-700 flex items-center justify-between gap-2 flex-shrink-0 bg-zinc-900">
          <div className="min-w-0 flex-1">
            {error && <p className="text-red-500 text-[11px] sm:text-sm truncate">{error}</p>}
            {success && <p className="text-green-500 text-[11px] sm:text-sm truncate">{success}</p>}
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button variant="outline" onClick={onClose} className="border-zinc-700 text-xs h-8 sm:h-9 px-3">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-red-600 hover:bg-red-700 text-xs h-8 sm:h-9 px-3 gap-1.5">
              <Save className="h-3.5 w-3.5" />{saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;
